import { useCallback, useEffect, useMemo, useState } from 'react';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import {
  createPositecClient,
  type AuthTenant,
  type AuthUser,
  type KdsLine,
  type KdsOrder,
  type PositecClient,
} from '@positec/api-client';

const TOKEN_KEY = 'positec_kds_token';
const CLOUD_URL = import.meta.env.VITE_API_URL ?? 'https://api.itpos.itrionsoft.com';
const REVERB_KEY = import.meta.env.VITE_REVERB_APP_KEY ?? "56ab8b79e8fa97e6b527be0f8a382e7412c4ff939f925ca3";
const REVERB_HOST = import.meta.env.VITE_REVERB_HOST ?? 'reverb.itpos.itrionsoft.com';
const REVERB_PORT = Number(import.meta.env.VITE_REVERB_PORT ?? 443);
const REVERB_SCHEME = import.meta.env.VITE_REVERB_SCHEME ?? 'https';
// Polling como fallback cuando Reverb no esté disponible. 30s en vez de 5s.
const POLL_MS = 30000;

(window as unknown as { Pusher: typeof Pusher }).Pusher = Pusher;

type Session = { user: AuthUser; tenant: AuthTenant | null };

function buildClient(): PositecClient {
  return createPositecClient({
    baseURL: CLOUD_URL,
    getToken: () => localStorage.getItem(TOKEN_KEY),
    onUnauthenticated: () => localStorage.removeItem(TOKEN_KEY),
  });
}

export default function App() {
  const [client] = useState<PositecClient>(() => buildClient());
  const [session, setSession] = useState<Session | null>(null);
  const [bootstrapping, setBootstrapping] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setBootstrapping(false);
      return;
    }
    client
      .me()
      .then((r) => setSession({ user: r.user, tenant: r.tenant }))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setBootstrapping(false));
  }, [client]);

  const onLogout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setSession(null);
  }, []);

  if (bootstrapping) {
    return (
      <div className="flex h-full items-center justify-center bg-slate-950 text-slate-300">
        Iniciando…
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-slate-950 text-slate-100">
      <Header session={session} onLogout={onLogout} />
      {!session ? (
        <LoginScreen client={client} onLogin={(s) => setSession(s)} />
      ) : (
        <KdsBoard client={client} tenantId={session.tenant?.id ?? null} />
      )}
    </div>
  );
}

function Header({ session, onLogout }: { session: Session | null; onLogout: () => void }) {
  return (
    <header className="flex items-center justify-between border-b border-slate-800 bg-slate-900 px-6 py-3">
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 rounded bg-emerald-500" />
        <span className="text-lg font-semibold">ITpos Cocina (KDS)</span>
        <span className="rounded bg-slate-800 px-2 py-0.5 text-xs text-slate-400">DESA</span>
        {session?.tenant && (
          <span className="rounded bg-slate-800 px-2 py-0.5 text-xs text-slate-300">
            {session.tenant.name}
          </span>
        )}
      </div>
      <div className="flex items-center gap-4">
        <Clock />
        {session && (
          <button
            onClick={onLogout}
            className="rounded border border-slate-700 px-3 py-1.5 text-xs hover:bg-slate-800"
          >
            Salir
          </button>
        )}
      </div>
    </header>
  );
}

function Clock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="font-mono text-sm tabular-nums text-slate-300">
      {now.toLocaleTimeString('es-ES')}
    </div>
  );
}

function LoginScreen({ client, onLogin }: { client: PositecClient; onLogin: (s: Session) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const r = await client.login({ email, password });
      localStorage.setItem(TOKEN_KEY, r.token);
      onLogin({ user: r.user, tenant: r.tenant });
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e?.response?.data?.message ?? e?.message ?? 'Error al iniciar sesión');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex flex-1 items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4 rounded-lg border border-slate-800 bg-slate-900/50 p-8">
        <h2 className="text-center text-xl font-semibold">Acceso KDS</h2>
        <label className="block text-sm">
          <span className="text-slate-300">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
          />
        </label>
        <label className="block text-sm">
          <span className="text-slate-300">Contraseña</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
          />
        </label>
        {error && <div className="rounded border border-red-700/50 bg-red-900/30 px-3 py-2 text-sm text-red-300">{error}</div>}
        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded bg-emerald-500 px-4 py-2 font-medium text-slate-900 hover:bg-emerald-400 disabled:opacity-50"
        >
          {submitting ? 'Entrando…' : 'Entrar'}
        </button>
      </form>
    </main>
  );
}

function KdsBoard({ client, tenantId }: { client: PositecClient; tenantId: string | null }) {
  const [orders, setOrders] = useState<KdsOrder[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [busyLines, setBusyLines] = useState<Set<string>>(new Set());
  const [realtime, setRealtime] = useState<'connecting' | 'connected' | 'disconnected'>('disconnected');

  const refresh = useCallback(async () => {
    try {
      const r = await client.kdsOrders();
      setOrders(r.orders);
      setLoadedAt(new Date(r.fetched_at));
      setError(null);
    } catch (err: unknown) {
      const e = err as { message?: string };
      setError(e?.message ?? 'Error al cargar comandas');
    }
  }, [client]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, [refresh]);

  // Reverb: empuje en tiempo real
  useEffect(() => {
    if (!tenantId || !REVERB_KEY) return;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;

    setRealtime('connecting');
    const echo = new Echo({
      broadcaster: 'reverb',
      key: REVERB_KEY,
      wsHost: REVERB_HOST,
      wsPort: REVERB_PORT,
      wssPort: REVERB_PORT,
      forceTLS: REVERB_SCHEME === 'https',
      enabledTransports: ['ws', 'wss'],
      authEndpoint: `${CLOUD_URL}/api/v1/broadcasting/auth`,
      auth: { headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' } },
    });

    const pusher = (echo.connector as unknown as { pusher: Pusher }).pusher;
    pusher.connection.bind('connected', () => setRealtime('connected'));
    pusher.connection.bind('disconnected', () => setRealtime('disconnected'));
    pusher.connection.bind('unavailable', () => setRealtime('disconnected'));

    const channel = echo.private(`kds.${tenantId}`);
    channel.listen('.kitchen.line.updated', () => {
      // Refrescamos cuando cualquier línea cambia. Más adelante podríamos
      // hacer un patch local optimizado.
      refresh();
    });

    return () => {
      echo.leave(`kds.${tenantId}`);
      echo.disconnect();
    };
  }, [tenantId, refresh]);

  const setBusy = (id: string, busy: boolean) => {
    setBusyLines((prev) => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const transition = useCallback(
    async (line: KdsLine, action: 'start' | 'ready' | 'served' | 'cancel') => {
      setBusy(line.id, true);
      try {
        if (action === 'start') await client.kdsStart(line.id);
        else if (action === 'ready') await client.kdsReady(line.id);
        else if (action === 'served') await client.kdsServed(line.id);
        else await client.kdsCancel(line.id);
        await refresh();
      } catch (err: unknown) {
        const e = err as { response?: { data?: { message?: string } } };
        setError(e?.response?.data?.message ?? 'Error al cambiar estado');
      } finally {
        setBusy(line.id, false);
      }
    },
    [client, refresh],
  );

  const grouped = useMemo(() => {
    const cols: Record<'pending' | 'started' | 'ready', { order: KdsOrder; line: KdsLine }[]> = {
      pending: [],
      started: [],
      ready: [],
    };
    for (const order of orders) {
      for (const line of order.lines) {
        if (line.preparation_status === 'pending') cols.pending.push({ order, line });
        else if (line.preparation_status === 'started') cols.started.push({ order, line });
        else if (line.preparation_status === 'ready') cols.ready.push({ order, line });
      }
    }
    cols.pending.sort((a, b) => b.line.waiting_seconds - a.line.waiting_seconds);
    cols.started.sort((a, b) => b.line.waiting_seconds - a.line.waiting_seconds);
    cols.ready.sort((a, b) => b.line.waiting_seconds - a.line.waiting_seconds);
    return cols;
  }, [orders]);

  return (
    <main className="flex flex-1 flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-800 bg-slate-900/50 px-6 py-2 text-xs text-slate-400">
        <div className="flex items-center gap-3">
          <span>
            {orders.length} ticket{orders.length === 1 ? '' : 's'} con comandas pendientes
          </span>
          <span
            className={
              realtime === 'connected'
                ? 'rounded bg-emerald-700/40 px-2 py-0.5 text-emerald-200'
                : realtime === 'connecting'
                ? 'rounded bg-amber-700/40 px-2 py-0.5 text-amber-200'
                : 'rounded bg-slate-700/40 px-2 py-0.5 text-slate-300'
            }
          >
            {realtime === 'connected' ? '⚡ realtime' : realtime === 'connecting' ? 'conectando…' : 'polling'}
          </span>
        </div>
        <div>
          {loadedAt && <>Actualizado {loadedAt.toLocaleTimeString('es-ES')}</>}
          {error && <span className="ml-3 text-red-400">{error}</span>}
        </div>
      </div>
      <div className="grid flex-1 grid-cols-1 gap-3 overflow-hidden p-3 md:grid-cols-3">
        <Column
          title="Pendiente"
          accent="bg-amber-500"
          items={grouped.pending}
          busy={busyLines}
          actions={[
            { label: 'Empezar', kind: 'primary', action: (l) => transition(l, 'start') },
            { label: 'Cancelar', kind: 'danger', action: (l) => transition(l, 'cancel') },
          ]}
        />
        <Column
          title="Preparando"
          accent="bg-sky-500"
          items={grouped.started}
          busy={busyLines}
          actions={[
            { label: 'Listo', kind: 'primary', action: (l) => transition(l, 'ready') },
            { label: 'Cancelar', kind: 'danger', action: (l) => transition(l, 'cancel') },
          ]}
        />
        <Column
          title="Listo"
          accent="bg-emerald-500"
          items={grouped.ready}
          busy={busyLines}
          actions={[
            { label: 'Entregado', kind: 'primary', action: (l) => transition(l, 'served') },
          ]}
        />
      </div>
    </main>
  );
}

type Action = {
  label: string;
  kind: 'primary' | 'danger';
  action: (line: KdsLine) => void;
};

function Column({
  title,
  accent,
  items,
  busy,
  actions,
}: {
  title: string;
  accent: string;
  items: { order: KdsOrder; line: KdsLine }[];
  busy: Set<string>;
  actions: Action[];
}) {
  return (
    <section className="flex h-full flex-col overflow-hidden rounded-lg border border-slate-800 bg-slate-900/30">
      <header className="flex items-center justify-between border-b border-slate-800 bg-slate-900 px-4 py-2">
        <div className="flex items-center gap-2">
          <span className={`h-3 w-3 rounded-full ${accent}`} />
          <span className="font-semibold">{title}</span>
        </div>
        <span className="rounded bg-slate-800 px-2 py-0.5 text-xs text-slate-400">{items.length}</span>
      </header>
      <div className="flex-1 space-y-2 overflow-y-auto p-3">
        {items.length === 0 && (
          <div className="rounded border border-dashed border-slate-700 px-3 py-6 text-center text-sm text-slate-500">
            Sin comandas
          </div>
        )}
        {items.map(({ order, line }) => (
          <Card key={line.id} order={order} line={line} busy={busy.has(line.id)} actions={actions} />
        ))}
      </div>
    </section>
  );
}

function Card({
  order,
  line,
  busy,
  actions,
}: {
  order: KdsOrder;
  line: KdsLine;
  busy: boolean;
  actions: Action[];
}) {
  const waitingMin = Math.floor(line.waiting_seconds / 60);
  const waitingSec = Math.floor(line.waiting_seconds % 60);
  const overdue = line.waiting_seconds > 600;

  return (
    <article
      className={`rounded-lg border p-3 ${
        overdue ? 'border-red-700/60 bg-red-950/30' : 'border-slate-700 bg-slate-900/60'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-base font-semibold leading-tight">
            {line.quantity}× {line.product_name}
          </div>
          {line.notes && (
            <div className="mt-1 text-xs text-amber-300">📝 {line.notes}</div>
          )}
        </div>
        <div className="text-right text-xs text-slate-400">
          {order.sales_location?.name && (
            <div className="font-mono text-slate-200">{order.sales_location.name}</div>
          )}
          {order.sales_center?.name && <div>{order.sales_center.name}</div>}
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between text-xs">
        <span className={overdue ? 'text-red-300' : 'text-slate-400'}>
          ⏱ {waitingMin}:{waitingSec.toString().padStart(2, '0')}
        </span>
        <span className="text-slate-500">{order.diners} cmnsl.</span>
      </div>
      <div className="mt-3 flex gap-2">
        {actions.map((a) => (
          <button
            key={a.label}
            disabled={busy}
            onClick={() => a.action(line)}
            className={`flex-1 rounded px-2 py-1.5 text-sm font-medium transition disabled:opacity-50 ${
              a.kind === 'primary'
                ? 'bg-emerald-500 text-slate-900 hover:bg-emerald-400'
                : 'border border-slate-700 text-slate-300 hover:bg-slate-800'
            }`}
          >
            {busy ? '…' : a.label}
          </button>
        ))}
      </div>
    </article>
  );
}
